import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { 
  DollarSign, ShoppingCart, TrendingUp, Clock, Utensils, BarChart3,
  ArrowUp, ArrowDown, CreditCard, RefreshCw
} from "lucide-react";
import { cn, formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split("T")[0]);

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/analytics/overview`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data);
    } catch (e) { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };

  const paymentMethodLabel = (m) => {
    const labels = { cash: "Cash", evc_plus: "EVC Plus", zaad: "Zaad", edahab: "E-Dahab", pay_at_cashier: "Cashier", pay_with_receipt: "Pay Later" };
    return labels[m] || m;
  };

  if (loading || !data) {
    return <AdminLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  const maxDayRevenue = Math.max(...data.sales_by_day.map(d => d.revenue), 1);
  const maxHourOrders = Math.max(...data.peak_hours.map(h => h.orders), 1);
  const maxItemQty = data.top_items.length > 0 ? data.top_items[0].quantity : 1;

  return (
    <AdminLayout>
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
        <p className="text-sm text-muted-foreground">Sales, peak hours and kitchen performance</p>
      </div>
      {/* Date Range */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" data-testid="date-from" />
        <span className="text-muted-foreground">to</span>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="date-to" />
        <Button onClick={fetchAnalytics} size="sm" data-testid="refresh-analytics"><RefreshCw className="w-4 h-4 mr-1" /> Update</Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={DollarSign} label="Revenue" value={formatCurrency(data.total_revenue)} color="text-green-600" bg="bg-green-50" />
        <KPICard icon={ShoppingCart} label="Total Orders" value={data.total_orders} color="text-blue-600" bg="bg-blue-50" />
        <KPICard icon={TrendingUp} label="Avg Order" value={formatCurrency(data.avg_order_value)} color="text-purple-600" bg="bg-purple-50" />
        <KPICard icon={CreditCard} label="Paid Orders" value={data.paid_orders} color="text-emerald-600" bg="bg-emerald-50" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Day */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" /> Daily Revenue
            </h3>
            {data.sales_by_day.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">No sales data</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {data.sales_by_day.slice(-14).map((d, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-20 shrink-0">{new Date(d.date).toLocaleDateString("en", { month: "short", day: "numeric" })}</span>
                    <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(d.revenue / maxDayRevenue) * 100}%` }} />
                    </div>
                    <span className="text-xs font-bold text-foreground w-16 text-right">{formatCurrency(d.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Peak Hours */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Peak Hours
            </h3>
            {data.peak_hours.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">No data</p>
            ) : (
              <div className="flex items-end gap-1.5 h-48">
                {Array.from({ length: 24 }, (_, h) => {
                  const hourData = data.peak_hours.find(p => p.hour === h);
                  const count = hourData?.orders || 0;
                  const pct = (count / maxHourOrders) * 100;
                  return (
                    <div key={h} className="flex-1 flex flex-col items-center justify-end h-full">
                      <div className={cn("w-full rounded-t transition-all", count > 0 ? "bg-primary" : "bg-muted")}
                        style={{ height: `${Math.max(pct, 2)}%` }} title={`${h}:00 - ${count} orders`} />
                      {h % 4 === 0 && <span className="text-xs text-muted-foreground mt-1">{h}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Payment Method */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-primary" /> Revenue by Payment
            </h3>
            {data.revenue_by_method.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">No data</p>
            ) : (
              <div className="space-y-3">
                {data.revenue_by_method.map((m, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-semibold text-foreground text-sm">{paymentMethodLabel(m.method)}</p>
                      <p className="text-xs text-muted-foreground">{m.count} orders</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">{formatCurrency(m.revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Type Split */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <Utensils className="w-4 h-4 text-primary" /> Order Types
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-3xl font-bold text-blue-600">{data.eat_in_count}</p>
                <p className="text-sm text-blue-700 font-medium">Eat In</p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-xl">
                <p className="text-3xl font-bold text-orange-600">{data.takeaway_count}</p>
                <p className="text-sm text-orange-700 font-medium">Takeaway</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Selling Items */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <ArrowUp className="w-4 h-4 text-green-500" /> Top Selling Items
            </h3>
            {data.top_items.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">No data</p>
            ) : (
              <div className="space-y-2">
                {data.top_items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                      i < 3 ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                    )}>{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{item.name}</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full mt-1">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${(item.quantity / maxItemQty) * 100}%` }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-foreground">{item.quantity} sold</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(item.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Worst Performing */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <ArrowDown className="w-4 h-4 text-red-500" /> Needs Attention
            </h3>
            {data.worst_items.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">No data</p>
            ) : (
              <div className="space-y-2">
                {data.worst_items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-red-50/50 rounded-lg">
                    <span className="text-sm text-foreground">{item.name}</span>
                    <div className="text-right">
                      <Badge className="bg-red-100 text-red-700 text-xs">{item.quantity} sold</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kitchen Performance */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Kitchen Performance (Avg Prep Time)
            </h3>
            {data.kitchen_performance.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">No preparation data yet. As orders are processed, prep times will appear here.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.kitchen_performance.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.count} orders tracked</p>
                    </div>
                    <Badge className={cn("text-xs",
                      item.avg_prep_minutes > 20 ? "bg-red-100 text-red-700" :
                      item.avg_prep_minutes > 10 ? "bg-amber-100 text-amber-700" :
                      "bg-green-100 text-green-700"
                    )}>
                      {item.avg_prep_minutes}m avg
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </AdminLayout>
  );
};

const KPICard = ({ icon: Icon, label, value, color, bg }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", bg)}>
        <Icon className={cn("w-5 h-5", color)} />
      </div>
      <div>
        <p className="text-xl font-bold text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default Analytics;
